import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useLenis } from "@/hooks/useLenis";
import { LocaleProvider } from "@/i18n/LocaleContext";
import { LOCALES, PT_LOCALES, DEFAULT_LOCALE, MARKET_CODES } from "@/i18n";
import { track, attribution } from "@/lib/track";
import OpenYourGym from "@/pages/OpenYourGym";
import DesignSystem from "@/pages/DesignSystem";
import ComingSoon from "@/pages/ComingSoon";
import CookiePolicy from "@/pages/CookiePolicy";
import PersonalTrainerStudio from "@/pages/PersonalTrainerStudio";
import { CookieConsent } from "@/components/CookieConsent";

// One shared application, one shared business logic — locale is a route prefix.
// OYG family routes: language locales + Africa Wave 1 country markets.
const OYG_ROUTE_LOCALES = [...LOCALES, ...MARKET_CODES];

const LocaleLanding = () => {
  const { locale } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!OYG_ROUTE_LOCALES.includes(locale)) {
      navigate({ pathname: "/open-your-gym", search: location.search }, { replace: true });
    }
  }, [locale, location.search, navigate]);

  useEffect(() => {
    track("page_view", { page: "open-your-gym" });
  }, [locale]);

  if (!OYG_ROUTE_LOCALES.includes(locale)) return null;
  return (
    <LocaleProvider locale={locale || DEFAULT_LOCALE}>
      <OpenYourGym />
    </LocaleProvider>
  );
};

const DefaultLanding = () => {
  const location = useLocation();
  useEffect(() => {
    track("page_view", { page: "open-your-gym" });
  }, [location.key]);
  return (
    <LocaleProvider locale={DEFAULT_LOCALE}>
      <OpenYourGym />
    </LocaleProvider>
  );
};

const DefaultLegal = () => (
  <LocaleProvider locale={DEFAULT_LOCALE}>
    <CookiePolicy />
  </LocaleProvider>
);

const PTStudioRoute = () => (
  <LocaleProvider locale={DEFAULT_LOCALE}>
    <PersonalTrainerStudio />
  </LocaleProvider>
);

// PT Studio family has its own locale set (adds Italian).
const LocalePTStudio = () => {
  const { locale } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!PT_LOCALES.includes(locale)) {
      navigate({ pathname: "/personal-trainer-studio", search: location.search }, { replace: true });
    }
  }, [locale, location.search, navigate]);
  if (!PT_LOCALES.includes(locale)) return null;
  return (
    <LocaleProvider locale={locale || DEFAULT_LOCALE}>
      <PersonalTrainerStudio />
    </LocaleProvider>
  );
};

// Cookie policy renders for the OYG locales plus Italian (PT Studio family)
// and the Africa Wave 1 country markets; the Italian body falls back to the
// English legal text until translated.
const LEGAL_LOCALES = [...LOCALES, "it", ...MARKET_CODES];

const LocaleLegal = () => {
  const { locale } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!LEGAL_LOCALES.includes(locale)) {
      navigate({ pathname: "/cookie-policy", search: location.search }, { replace: true });
    }
  }, [locale, location.search, navigate]);
  if (!LEGAL_LOCALES.includes(locale)) return null;
  return (
    <LocaleProvider locale={locale || DEFAULT_LOCALE}>
      <CookiePolicy />
    </LocaleProvider>
  );
};

function App() {
  useLenis();
  // First-touch attribution capture on entry — persists campaign params for
  // the whole tab session before any navigation can drop them.
  useEffect(() => {
    attribution();
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<ComingSoon />} />
          <Route path="/open-your-gym" element={<DefaultLanding />} />
          <Route path="/:locale/open-your-gym" element={<LocaleLanding />} />
          <Route path="/cookie-policy" element={<DefaultLegal />} />
          <Route path="/:locale/cookie-policy" element={<LocaleLegal />} />
          <Route path="/personal-trainer-studio" element={<PTStudioRoute />} />
          <Route path="/:locale/personal-trainer-studio" element={<LocalePTStudio />} />
          <Route path="/design-system" element={<DesignSystem />} />
          <Route path="*" element={<Navigate to="/open-your-gym" replace />} />
        </Routes>
        <CookieConsent />
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}

export default App;
